import { store } from '../../state/store';
import type { McpToolDef } from '../../types';
import { filterValidTools } from '../../utils/tool-validation';
import { resolveSpeechLocale } from '../../utils/locale';
import { ChatOrchestratorBase, type Constructor } from './orchestrator-base';

export function withToolsHandling<TBase extends Constructor<ChatOrchestratorBase>>(Base: TBase) {
    return class extends Base {
        protected getFilteredTools(): McpToolDef[] {
            const { tools, config } = store.getState();
            const { allowedTools, dangerousTools } = config;

            if (allowedTools === undefined && dangerousTools === undefined) {
                return filterValidTools(tools);
            }

            const combinedSet = new Set([...(allowedTools ?? []), ...(dangerousTools ?? [])]);
            return filterValidTools(tools.filter((tool) => tool.id === 0 || combinedSet.has(tool.id)));
        }

        protected isAllowedToolName(name: string): boolean {
            return this.getFilteredTools().some((tool) => tool.name === name);
        }

        /** Forms are enabled only when every named tool's own setting allows them. */
        protected isFormEnabledForTools(toolNames: string[]): boolean {
            const { tools } = store.getState();
            return toolNames.every((name) => tools.find((tool) => tool.name === name)?.formEnabled !== false);
        }

        protected buildMissingRequiredFormContent(
            pendingToolUses: Array<{ id: string; name: string; input: Record<string, unknown> }>,
        ): string | null {
            for (const toolUse of pendingToolUses) {
                const enrichedInput = this.enrichToolInputFromContext(toolUse.name, toolUse.input);
                const missingRequiredFields = this.getMissingRequiredFields(toolUse.name, enrichedInput);
                if (missingRequiredFields.length === 0) continue;

                const toolDef = store.getState().tools.find((tool) => tool.name === toolUse.name);
                const properties = (toolDef?.inputSchema as Record<string, unknown> | undefined)?.properties as
                    | Record<string, Record<string, unknown>>
                    | undefined;
                const labels = Object.fromEntries(
                    missingRequiredFields.map((fieldName) => {
                        const prop = properties?.[fieldName];
                        const label =
                            (typeof prop?.title === 'string' && prop.title) ||
                            (typeof prop?.description === 'string' && prop.description) ||
                            fieldName;
                        return [fieldName, label];
                    }),
                );

                const instruction = store.getState().config.language === 'he'
                    ? 'אנא מלא את הטופס כדי להמשיך.'
                    : 'Please fill in the form to continue.';

                return `${instruction}\n\n\`\`\`form\n${JSON.stringify({
                    tool: toolUse.name,
                    fields: enrichedInput,
                    fieldNames: missingRequiredFields,
                    labels,
                })}\n\`\`\``;
            }

            return null;
        }

        protected buildMissingRequiredTextPrompt(
            pendingToolUses: Array<{ id: string; name: string; input: Record<string, unknown> }>,
            accumulated: string,
        ): string | null {
            for (const toolUse of pendingToolUses) {
                const enrichedInput = this.enrichToolInputFromContext(toolUse.name, toolUse.input);
                const missingRequiredFields = this.getMissingRequiredFields(toolUse.name, enrichedInput);
                if (missingRequiredFields.length === 0) continue;

                const toolDef = store.getState().tools.find((tool) => tool.name === toolUse.name);
                const properties = (toolDef?.inputSchema as Record<string, unknown> | undefined)?.properties as
                    | Record<string, Record<string, unknown>>
                    | undefined;

                const fieldLabels = missingRequiredFields.map((fieldName) => {
                    const prop = properties?.[fieldName];
                    return (typeof prop?.title === 'string' && prop.title) ||
                        (typeof prop?.description === 'string' && prop.description) ||
                        fieldName;
                });

                const isHebrew = resolveSpeechLocale(accumulated) === 'he';
                if (isHebrew) {
                    const list = fieldLabels.map((l) => `- ${l}`).join('\n');
                    return `כדי להמשיך, אנא ספק את הפרטים הבאים:\n${list}`;
                } else {
                    const list = fieldLabels.map((l) => `- ${l}`).join('\n');
                    return `To continue, please provide the following:\n${list}`;
                }
            }

            return null;
        }

        private getMissingRequiredFields(toolName: string, input: Record<string, unknown>): string[] {
            const toolDef = store.getState().tools.find((tool) => tool.name === toolName);
            const required = Array.isArray((toolDef?.inputSchema as Record<string, unknown> | undefined)?.required)
                ? ((toolDef?.inputSchema as Record<string, unknown>).required as string[])
                : [];

            return required.filter((fieldName) => {
                const value = input[fieldName];
                return value === undefined || value === null || value === '';
            });
        }

        protected isSystemTool(technicalName: string): boolean {
            if (['get_full_tool_result', 'get_full_previous_message', 'Analyze_Data', 'Data_Analyst'].includes(technicalName)) {
                return true;
            }
            const tool = store.getState().tools.find((t) => t.name === technicalName);
            return tool?.id === 0;
        }

        protected resolveToolDisplayName(technicalName: string): string {
            const tool = store.getState().tools.find((t) => t.name === technicalName);
            return tool?.title || technicalName;
        }

        // Implemented in orchestrator.ts — declared here only so this mixin's TS
        // types check without importing the whole orchestrator (would be circular).
        protected enrichToolInputFromContext(_toolName: string, _input: Record<string, unknown>): Record<string, unknown> {
            throw new Error('enrichToolInputFromContext must be provided by ChatOrchestrator');
        }
    };
}
